import React from 'react'
import { AppBasicTable } from "@components"
import { avgGapColumn, dataColumn, websiteColumn } from "Pages/AnalyzersV2/@components/Table/columns"
import AnalyzerTableToolbar from "Pages/AnalyzersV2/@components/Table/Toolbar"
import AnalyzersLegendToolbar from "Pages/AnalyzersV2/@components/Table/Toolbar/Legend"
import { postExcel, stringToDate } from '@ui-seeker/utils'
import { BACKEND_APIS } from "@constants"
import { analyzerFilters } from "Pages/AnalyzersV2/@signals"
import { useToast } from "@hooks"
import AnalyzerHeaderTable from "Pages/AnalyzersV2/@components/Table/columns/Data/Header"
import { dsColorNew } from "@palette"
import { isMainRow } from "Pages/AnalyzersV2/@components/Table/@utils"


export default function AnalyzersTable(
	{
		tableData,
		columns,
		evolutionData,
		isRating = false,
		isBrokers = false,
		hideIcon = false,
		isPosition = false,
		mainName
	}) {
	const { showToast } = useToast()
	let rowIndex = 0
	const isWeekend = date => {
		// The pinned "Avg gap" column has no date behind it.
		if (!date) return false

		const day = stringToDate(date).getDay()
		return day === 0 || day === 6
	}

	// Position pins "Avg gap" right after the website column, so the date columns
	// start one slot further right.
	const WEBSITE_COLUMN_WIDTH = 150
	const AVG_GAP_COLUMN_WIDTH = 90
	const firstDateIndex = isPosition ? 2 : 1
	const isAvgGapColumn = rowIdx => isPosition && rowIdx === 1
	const stickyLeft = rowIdx => rowIdx === 0
		? 0
		: isAvgGapColumn(rowIdx) ? WEBSITE_COLUMN_WIDTH : undefined
	const isSticky = rowIdx => stickyLeft(rowIdx) !== undefined

	return <AppBasicTable
		Components={{
			Title: () => <AnalyzerTableToolbar isRating={isRating} isPosition={isPosition} />
		}}
		onDownload={() => postExcel(`${BACKEND_APIS.DASHBOARD}/excel`, analyzerFilters.value)}
		setHeaderCellProps={(_, rowIdx) => ({
			sx: {
				...isSticky(rowIdx) && {
					position: 'sticky',
					left: stickyLeft(rowIdx),
					zIndex: 3,
					...isAvgGapColumn(rowIdx) && { minWidth: AVG_GAP_COLUMN_WIDTH },
				},
				p: .5,
				textAlign: rowIdx === 0 && "left !important",
				backgroundColor: dsColorNew.tableHead,
				pl: rowIdx === 0 && 1.75,
				...isWeekend(columns[rowIdx - firstDateIndex]) && {
					boxShadow: `inset 0 -3px 0 ${dsColorNew.dangerSoft}`,
					'& .MuiTypography-root:last-of-type': { color: dsColorNew.dangerBorder },
				},
			}
		})}
		setBodyCellProps={(_, row, idx, rowIdx) => {
			if (rowIdx === 0) {
				rowIndex++
			}
			const highlightMain = isPosition && isMainRow(row)
			const sticky = isSticky(rowIdx)
			return {
				sx: {
					position: sticky && 'sticky',
					left: sticky ? stickyLeft(rowIdx) : 0,
					p: rowIdx === 0 ? 0 : '7px 6px',
					backgroundColor: highlightMain
						? dsColorNew.pillActiveBg
						: (rowIndex % 2 === 0 ? dsColorNew.tableRowAlt : dsColorNew.white),
					...sticky && { borderRight: `1px solid ${dsColorNew.grayBorder}` },
					// Only position pins a second column, and only there do the pinned
					// cells need to outrank the scrolling ones.
					...isPosition && sticky && {
						zIndex: 2,
						...isAvgGapColumn(rowIdx) && { minWidth: AVG_GAP_COLUMN_WIDTH },
					},
				}
			}
		}}
		headers={[[
			{ label: isBrokers ? "Provider" : "Website" },
			...isPosition ? [{ label: "Avg gap" }] : [],
			...columns.map(label => ({
				render: () => <AnalyzerHeaderTable label={label} {...evolutionData}/>
			}))
		]]}
		options={{
			onDownload: () => showToast.promise(postExcel(`${BACKEND_APIS.DASHBOARD}/excel`, analyzerFilters.value))
		}}
		footer={<AnalyzersLegendToolbar isRating={isRating}/>}
		cardProps={{ 'data-tour-id': 'tour-price-table' }}
		data={tableData}
		columns={[
			websiteColumn(isBrokers, hideIcon, isPosition),
			...isPosition ? [avgGapColumn(mainName)] : [],
			// Position drops the per-cell evolution button: the column header still
			// opens the evolution for the whole date.
			...columns?.map(c => dataColumn(c, evolutionData, { isRating, isPosition }))
		]}
	/>
}